import { Group, Anchor, ActionIcon, Tooltip } from "@mantine/core";
import { IconPhone, IconMail } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { primaryPhone, primaryEmail } from "../_Shared/contact";

/**
 * Tap-to-call / tap-to-email affordance for any surface a person appears on — the single most
 * useful action when the CRM is open on a phone. Renders the primary phone (and optionally email)
 * as native `tel:`/`mailto:` links.
 *
 * `stopPropagation` is on by default: these almost always live inside a click-through card/row, and
 * without it a tap on the phone would navigate to the profile instead of placing the call.
 *
 *   variant="row"    → icon + value text, inline (default; good in a card body)
 *   variant="icons"  → 44px icon buttons only (good in a tight header/toolbar)
 */
export default function ContactActions({
  contact,
  variant = "row",
  showEmail = false,
  size = "sm",
  stopPropagation = true,
  ...groupProps
}) {
  const { t } = useTranslation("Veft");
  const phone = primaryPhone(contact);
  const email = showEmail ? primaryEmail(contact) : null;
  if (!phone && !email) return null;

  const halt = stopPropagation ? (e) => e.stopPropagation() : undefined;

  if (variant === "icons") {
    return (
      <Group gap={4} wrap="nowrap" {...groupProps}>
        {phone && (
          <Tooltip label={`${t("call")} ${phone.number}`}>
            <ActionIcon component="a" href={`tel:${phone.number}`} onClick={halt}
              variant="light" color="gray" size="xl" aria-label={`${t("call")} ${phone.number}`}>
              <IconPhone size={16} />
            </ActionIcon>
          </Tooltip>
        )}
        {email && (
          <Tooltip label={email.address}>
            <ActionIcon component="a" href={`mailto:${email.address}`} onClick={halt}
              variant="light" color="gray" size="xl" aria-label={`${t("send_email")} ${email.address}`}>
              <IconMail size={16} />
            </ActionIcon>
          </Tooltip>
        )}
      </Group>
    );
  }

  return (
    <Group gap="md" wrap="wrap" {...groupProps}>
      {phone && (
        <Anchor href={`tel:${phone.number}`} size={size} onClick={halt} underline="never"
          style={{ display: "inline-flex", alignItems: "center", gap: 4, fontVariantNumeric: "tabular-nums" }}>
          <IconPhone size={14} style={{ flexShrink: 0 }} /> {phone.number}
        </Anchor>
      )}
      {email && (
        <Anchor href={`mailto:${email.address}`} size={size} onClick={halt} underline="never"
          style={{ display: "inline-flex", alignItems: "center", gap: 4, minWidth: 0 }}>
          <IconMail size={14} style={{ flexShrink: 0 }} />
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{email.address}</span>
        </Anchor>
      )}
    </Group>
  );
}
